/**
 * Styles CF definitions (DL-61877 epic) — one per widget type used by the inline-edit e2e
 * suite, plus a NestedAllowedValue mother/child pair and a brand/group/category-scoped
 * "Advanced" field. Shape mirrors the real `/api/meta/styles/custom-fields` response
 * consumed by `useStyleCustomFieldDefinitionsFromMeta` (`src/features/styles/api/`).
 *
 * `allowedValues[].id` is deliberately a STRING (`'7031'`) while `allowedValues[].parentId`
 * and every row-level allowed-value id (`customFields[].value.id` / `.values[].id` on
 * `stylesData`, e.g. `7032`, `7051`) are numeric — this is NOT a mock inconsistency to
 * "fix" by unifying the type. It is a verified, faithful mirror of the real backend
 * contract: `useStyleCustomFieldDefinitionsFromMeta.ts`'s own `MetaItem` type (annotated
 * "Mirrors backend StyleCustomFieldMetaItem") types `allowedValues[].id` as `string` and
 * `parentId` as `number | null` in that SAME array, and its `toCustomFieldDefinition`
 * mapper explicitly does `id: Number(av.id)` to bridge the two — proof the real API
 * genuinely returns a string id in the meta response while every row-level reference to
 * that same id is numeric. A reverse `allowedValues.find(v => v.id === someNumericId)`
 * lookup must convert one side first (`String(someNumericId)`), exactly as the real
 * transform code already does — never assume the two ids share a type.
 *
 * Split out of `styles.ts` (DL-62594) purely to keep that file under the repo's 800-line cap
 * (`.eslintrc.js` `max-lines`) — same pattern as `itemsFixtureData.ts` for `items.ts`. No
 * behavior change, same values, same shapes.
 */
export const styleCustomFieldDefinitions = [
  {
    id: 701,
    name: 'Fabric Notes',
    position: 1,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 255,
  },
  {
    id: 702,
    name: 'Delivery Date',
    position: 2,
    type: 1, // Date
  },
  {
    id: 703,
    name: 'Fit Type',
    position: 3,
    type: 0, // AllowedValue (single-select)
    isMultipleAllowedValue: false,
    allowedValues: [
      { id: '7031', value: '7031', displayName: 'Slim' },
      { id: '7032', value: '7032', displayName: 'Regular' },
      { id: '7033', value: '7033', displayName: 'Loose' },
    ],
  },
  {
    id: 704,
    name: 'Fabric Blend',
    position: 4,
    type: 0, // AllowedValue (multi-select)
    isMultipleAllowedValue: true,
    // 12 options (DL-62704/DL-62718, Testmo 2339495) — deliberately more than fit in the
    // dropdown's visible listbox height, so its "Set empty" FIXED footer genuinely has an
    // internally-scrolling option list to sit below (the Testmo case's own point: the footer
    // must stay visible and non-overlapping while the list scrolls).
    allowedValues: [
      { id: '7041', value: '7041', displayName: 'Cotton' },
      { id: '7042', value: '7042', displayName: 'Polyester' },
      { id: '7043', value: '7043', displayName: 'Wool' },
      { id: '7044', value: '7044', displayName: 'Elastane' },
      { id: '7045', value: '7045', displayName: 'Silk' },
      { id: '7046', value: '7046', displayName: 'Linen' },
      { id: '7047', value: '7047', displayName: 'Nylon' },
      { id: '7048', value: '7048', displayName: 'Viscose' },
      { id: '7049', value: '7049', displayName: 'Cashmere' },
      { id: '70410', value: '70410', displayName: 'Denim' },
      { id: '70411', value: '70411', displayName: 'Corduroy' },
      { id: '70412', value: '70412', displayName: 'Velvet' },
    ],
  },
  {
    id: 705,
    name: 'Print Type',
    position: 5,
    type: 3, // NestedAllowedValue — mother (no parentCustomFieldId)
    allowedValues: [
      { id: '7051', value: '7051', displayName: 'Screen Print', parentId: null },
      { id: '7052', value: '7052', displayName: 'Embroidery', parentId: null },
    ],
  },
  {
    id: 706,
    name: 'Print Placement',
    position: 6,
    type: 3, // NestedAllowedValue — child of 705
    parentCustomFieldId: 705,
    parentCustomFieldName: 'Print Type',
    allowedValues: [
      { id: '7061', value: '7061', displayName: 'Chest', parentId: 7051 },
      { id: '7062', value: '7062', displayName: 'Sleeve', parentId: 7051 },
      { id: '7063', value: '7063', displayName: 'Back', parentId: 7052 },
      { id: '7064', value: '7064', displayName: 'Collar', parentId: 7052 },
    ],
  },
  {
    id: 707,
    name: 'Sustainability Rating',
    position: 7,
    type: 0, // AllowedValue (single-select), scoped — DL-61911
    isMultipleAllowedValue: false,
    hasGroupScope: true,
    hasCategoryScope: true,
    allowedValues: [
      { id: '7071', value: '7071', displayName: 'Bronze' },
      { id: '7072', value: '7072', displayName: 'Silver' },
      { id: '7073', value: '7073', displayName: 'Gold' },
    ],
  },
  {
    // DL-62704/DL-62715, Testmo 2339497 — optional Numeric CF for the "Set empty" per-field-type
    // behavior spec; no existing Numeric-type definition was seeded here before.
    id: 708,
    name: 'Target Weight (g)',
    position: 8,
    type: 4, // Numeric
    allowedDecimals: 0,
  },
  {
    // DL-66146 (Testmo 888418/888419) — a THIRD hierarchy level (grandchild of 706 "Print
    // Placement") so the Styles table's column-ordering fix (parent → child → grandchild,
    // `useCustomFieldColumns.ts`'s `insertWithDescendants`) has a real 3-deep chain to prove
    // recursion, not just the 2-level 705/706 pair. "Alpha Field" / "Zeta Field" (710/711) are
    // plain non-hierarchy CFs registered deliberately out of alphabetical order, so
    // 888419 can prove they stay correctly interleaved (alphabetically) around the whole
    // hierarchy group rather than accidentally passing because they were already adjacent.
    id: 709,
    name: 'Print Finish',
    position: 9,
    type: 3, // NestedAllowedValue — grandchild of 706 (child of 705)
    parentCustomFieldId: 706,
    parentCustomFieldName: 'Print Placement',
    allowedValues: [
      { id: '7091', value: '7091', displayName: 'Matte', parentId: 7061 },
      { id: '7092', value: '7092', displayName: 'Glossy', parentId: 7061 },
      { id: '7093', value: '7093', displayName: 'Textured', parentId: 7062 },
    ],
  },
  {
    id: 710,
    name: 'Zeta Field',
    position: 10,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 100,
  },
  {
    id: 711,
    name: 'Alpha Field',
    position: 11,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 100,
  },
  {
    // DL-66269/DL-66289 (Testmo dl-58173) — a `diffPerColor` field. Unlike brand/group/category
    // scope (CF 707), diffPerColor/diffPerSize fields are UNCONDITIONALLY stripped out of
    // `customFieldDefinitions` client-side (`useStyleCustomFieldDefinitionsFromMeta.ts`:
    // `data.filter(cf => !cf.diffPerColor && !cf.diffPerSize)`), regardless of the
    // `EnableAdvancedCustomFields` flag/module gate — so this field must never appear in the
    // column selector, as a table column, as a filter option, or in the bulk-edit modal.
    // Additive across both epics' branches by design (see DL-66269's own PR/epic history) — if
    // this ever collides on merge with an identical block, keep either copy, they're the same.
    id: 712,
    name: 'Color Fastness Notes',
    position: 12,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 255,
    diffPerColor: true,
  },
  {
    // DL-66269/DL-66289 — the `diffPerSize` counterpart of CF 712, same unconditional exclusion.
    id: 713,
    name: 'Size Grading Notes',
    position: 13,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 255,
    diffPerSize: true,
  },
  {
    // DL-66289 (Testmo #888615) — a CF with BOTH diffPerColor AND diffPerSize true. Distinct
    // from CF 712/713 (single-flag) so this story doesn't duplicate either matrix cell.
    id: 714,
    name: 'Care Variant Notes',
    position: 14,
    type: 2, // Text
    isMultiLine: false,
    maxChar: 255,
    diffPerColor: true,
    diffPerSize: true,
  },
  {
    // DL-66669 epic (Testmo folder 93168 part 7 of 9), story DL-66675 (Testmo #754948) — a
    // MULTILINE Text CF. No prior definition here had `isMultiLine: true`; needed to prove/refute
    // the epic's own presumed `cf_{id}_description` URL/testid id for a multiline field (see
    // `stylesCfFilterTypesFixtureData.ts`'s header for the verified-against-source finding).
    id: 715,
    name: 'Care Instructions',
    position: 15,
    type: 2, // Text
    isMultiLine: true,
    maxChar: 500,
  },
];
